import { Col, Row } from 'antd';
import { Link, graphql, useStaticQuery } from 'gatsby';
import CopyrightIcon from '@material-ui/icons/Copyright';
import React from 'react';
import styled from '@emotion/styled';

import { Link14 } from './link';
import TechnologyPartners from './technology-partners';
import SocialBarComponent from './social-bar';

import LogoFooter from '../../resources/images/hsf/hsf-logo-bw.png';
import theme from '../../theme';
import { useI18NContext } from '../../i18n';
import { copyrightLanguage, programShortName } from '../../siteContent';
import { getRealNodes } from '../../utils/contentFilter';

type FooterGroup = {
  navigation_group_name: string;
  navigation_group_slug: string;
  translations: [{ display_name: string }];
  navigation_group_items: FooterItem[];
};

type FooterItem = {
  navigation_item_name: string;
  navigation_item_slug: string;
  translations: [{ display_name: string }];
  sort: number;
};

const Footer = styled.footer`
  padding-top: 80px;
  background-color: #252525;
  color: ${theme.colorsWhite};
  ul,
  ol {
    list-style: none;
  }
  @media (max-width: ${theme.screenXsMax}) {
    padding: 70px 20px 0 20px;
  }
`;

export const InternalContainer = styled.div`
  max-width: ${theme.screenLgMax};
  margin: auto;
`;

const TopFooter = styled.div`
  padding-bottom: 35px;
  border-bottom: 1px solid ${theme.colorsWhite};
`;

const BottomFooter = styled.div`
  padding: 35px 0;
`;

const LogoWrapper = styled(Link)`
  &.techPartner {
    display: inline-block;
    height: 50px;
    width: 50px;
    margin-top: 16px;
  }
  @media (max-width: ${theme.screenLgMax}) {
    display: none;
  }
`;

export const FooterImageContainer = styled.img`
  height: 100%;
  cursor: pointer;

  &.techPartnerLogo {
    filter: grayscale(100%);
  }
`;

const StyledLogo = styled(FooterImageContainer)`
  max-width: 100%;
  height: 48px;
  padding-right: 8px;
`;

const CopyRightWrapper = styled.div`
  @media (max-width: ${theme.screenMdMax}) {
    text-align: center;
  }
`;

const CopyRightIcon = styled(CopyrightIcon)`
  font-size: 12px;
  opacity: 0.3;
  padding-right: 3px;
`;

const CopyRightText = styled.span`
  font-size: 14px;
  opacity: 0.5;
  vertical-align: top;
  display: inline-block;
  line-height: 25px;
`;

const FooterMenu = styled.ul`
  &.primary {
    font-weight: bold;
    opacity: 1;
    li {
      padding-bottom: 30px;
      opacity: 1;
    }
  }
  li:first-of-type {
    font-weight: bold;
    opacity: 1;
  }
`;

const FooterMenuItem = styled.li`
  font-size: 18px;
  letter-spacing: -0.32px;
  line-height: 23px;
  padding-bottom: 20px;
  opacity: 0.5;
`;

const MenuItem = styled.span`
  color: ${theme.colorsWhite};
  text-decoration: none;
`;

const MenuItemLink = styled.a`
  color: ${theme.colorsWhite};
  text-decoration: none;
  &:visited {
    color: ${theme.colorsWhite};
  }
  &:hover {
    color: ${theme.colorsSecondaryBlue};
    text-decoration: underline;
    font-weight: bold;
  }
`;

const FooterLink = styled(Link14)`
  color: ${theme.colorsWhite};
  opacity: 0.5;
  margin-right: 8px;
  white-space: nowrap;

  &:visited {
    color: ${theme.colorsWhite};
  }
  &:hover {
    color: ${theme.colorsSecondaryBlue};
    text-decoration: underline;
    font-weight: bold;
  }

  @media (max-width: ${theme.screenMdMax}) {
    display: block;
    text-align: center;
    margin-bottom: 25px;
  }
`;

const AligningCol = styled(Col)`
  text-align: center;
`;

const FooterComponent = () => {
  const data = useStaticQuery(
    graphql`
      query {
        footer: directusNavigation(navigation_type: { navigation_type_name: { eq: "footer" } }) {
          navigation_groups {
            navigation_group_items {
              navigation_item_name
              navigation_item_slug
              translations {
                language
                display_name
              }
              sort
            }
            navigation_group_name
            navigation_group_slug
            translations {
              language
              display_name
            }
            sort
          }
        }
        social: allDirectusSocialMedia(sort: { fields: sort }) {
          nodes {
            social_media_link
            social_media_type {
              social_media_name
            }
          }
        }
        logos: directusLogo {
          enable_right_footer_logo
          right_footer_logo {
            data {
              full_url
            }
          }
          left_footer_logo {
            data {
              full_url
            }
          }
          tenant_logo {
            data {
              full_url
            }
          }
        }
      }
    `
  );

  const { translate, translateSlug } = useI18NContext();
  const socialMediaItems = getRealNodes(data.social);
  const leftFooterLogoUrl = data?.logos?.left_footer_logo?.data?.full_url;
  const rightFooterLogoUrl = data?.logos?.right_footer_logo?.data?.full_url;
  const enableRightFooterLogo = data?.logos?.enable_right_footer_logo && rightFooterLogoUrl;

  const footerSpacings = {
    copyright: {
      xs: { span: 24, order: 3 },
      lg: { span: 8, order: 1 },
      xl: { span: 7, order: 1 },
    },
    policies: {
      xs: { span: 24, order: 2 },
      lg: { span: 4, order: 2 },
    },
    social: {
      xs: { span: 24, order: 1 },
      lg: { span: 12, order: 3 },
      xl: { span: 10, order: 3 },
    },
    footerLogo: { lg: 0, xl: { span: 3, order: 4 } },
  };

  // If a footer logo is enabled for the tenant, shift the spacing of footer items.
  if (enableRightFooterLogo) {
    footerSpacings.copyright.lg = { span: 7, order: 1 };
    footerSpacings.copyright.xl = { span: 6, order: 1 };
    footerSpacings.policies.lg = { span: 3, order: 2 };
    footerSpacings.social.lg = { span: 11, order: 3 };
    footerSpacings.social.xl = { span: 6, order: 3 };
  }

  return (
    <Footer>
      <InternalContainer>
        <TopFooter>
          {data.footer && (
            <Row justify="space-between">
              {data.footer.navigation_groups.map((group: FooterGroup, index: number) => {
                return (
                  <Col key={index} lg={6} xs={12}>
                    <FooterMenu className={index === 0 ? 'primary' : ''}>
                      <FooterMenuItem key={group.navigation_group_name}>
                        {group?.navigation_group_slug ? (
                          <MenuItemLink href={translateSlug(group.navigation_group_slug)}>
                            {translate(group).display_name}
                          </MenuItemLink>
                        ) : (
                          <MenuItem>{translate(group).display_name}</MenuItem>
                        )}
                      </FooterMenuItem>
                      {group.navigation_group_items.map((item) => {
                        return (
                          <FooterMenuItem key={item.navigation_item_name}>
                            <MenuItemLink href={translateSlug(item.navigation_item_slug)}>
                              {translate(item).display_name}
                            </MenuItemLink>
                          </FooterMenuItem>
                        );
                      })}
                    </FooterMenu>
                  </Col>
                );
              })}
            </Row>
          )}
          <Row justify="center">
            <TechnologyPartners headingStyles={`color: ${theme.colorsWhite}; opacity: 0.5;`} />
          </Row>
        </TopFooter>

        <BottomFooter>
          <Row justify="space-around">
            <AligningCol xl={3} lg={0}>
              <LogoWrapper to="/">
                <StyledLogo src={leftFooterLogoUrl ?? LogoFooter} alt={`to ${programShortName} Home`} />
              </LogoWrapper>
            </AligningCol>
            <Col {...footerSpacings.copyright}>
              <CopyRightWrapper>
                <CopyRightIcon />
                <CopyRightText>{copyrightLanguage}</CopyRightText>
              </CopyRightWrapper>
            </Col>
            <Col {...footerSpacings.policies}>
              <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink to="/terms-of-use">Terms of Use</FooterLink>
            </Col>
            {socialMediaItems && (
              <Col {...footerSpacings.social}>
                <SocialBarComponent socialItems={socialMediaItems} isHeader={false} />
              </Col>
            )}
            {enableRightFooterLogo && (
              <AligningCol {...footerSpacings.footerLogo}>
                <LogoWrapper to="/">
                  <StyledLogo src={rightFooterLogoUrl} alt={`to ${programShortName} Home`} />
                </LogoWrapper>
              </AligningCol>
            )}
          </Row>
        </BottomFooter>
      </InternalContainer>
    </Footer>
  );
};

export default FooterComponent;
